import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import {
  SquaresFour,
  GlobeHemisphereEast,
  CloudSun,
  ChartLineUp,
  Export,
  Terminal,
  Newspaper,
  TrafficCone,
  SignOut,
  BookOpen,
  MapTrifold,
  Gear,
  Bell,
  GitDiff,
  Snowflake,
  Barn,
  Coin,
  Wrench,
  Cow,
  GraduationCap,
  CalendarBlank,
  Virus,
  Compass,
  Microphone,
  Flower,
  Trophy,
  Clock,
  Sparkle,
  Handshake,
  BookBookmark,
  Leaf,
  Heart,
  Fire,
  Exam,
} from "@phosphor-icons/react";

import Dashboard from "@/pages/Dashboard";
import Satellite from "@/pages/Satellite";
import Weather from "@/pages/Weather";
import Correlation from "@/pages/Correlation";
import ContentStudio from "@/pages/ContentStudio";
import Pipeline from "@/pages/Pipeline";
import PublicInsights from "@/pages/PublicInsights";
import InsightPost from "@/pages/InsightPost";
import QuickStatus from "@/pages/QuickStatus";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Lexikon from "@/pages/Lexikon";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import AGB from "@/pages/AGB";
import RegionMap from "@/pages/RegionMap";
import Settings from "@/pages/Settings";
import Forecast from "@/pages/Forecast";
import Compare from "@/pages/Compare";
import Alerts from "@/pages/Alerts";
import ShareView from "@/pages/ShareView";
import MyFields from "@/pages/MyFields";
import Commodities from "@/pages/Commodities";
import Maschinen from "@/pages/Maschinen";
import Grassland from "@/pages/Grassland";
import Academy from "@/pages/Academy";
import FructanWidget from "@/pages/FructanWidget";
import Planner from "@/pages/Planner";
import DiseaseWatch from "@/pages/DiseaseWatch";
import Entdecken from "@/pages/Entdecken";
import Podcast from "@/pages/Podcast";
import Impact from "@/pages/Impact";
import Heritage from "@/pages/Heritage";
import Leaderboard from "@/pages/Leaderboard";
import Bees from "@/pages/Bees";
import FieldReport from "@/pages/FieldReport";
import SchoolEnter from "@/pages/SchoolEnter";
import SchoolAdmin from "@/pages/SchoolAdmin";
import Marketplace from "@/pages/Marketplace";
import MarketAcademy from "@/pages/MarketAcademy";
import DailyToday from "@/pages/DailyToday";
import DailyStudio from "@/pages/DailyStudio";
import Journal from "@/pages/Journal";
import EmbedHub from "@/pages/EmbedHub";
import Wrapped from "@/pages/Wrapped";
import SchoolGarden from "@/pages/SchoolGarden";
import SchoolGardenMap from "@/pages/SchoolGardenMap";
import Feldfruechte from "@/pages/Feldfruechte";
import Glossary from "@/pages/Glossary";
import Waldbrand from "@/pages/Waldbrand";
import LernQuiz from "@/pages/LernQuiz";
import PulseWidget from "@/pages/widgets/PulseWidget";
import WildfireWidget from "@/pages/widgets/WildfireWidget";
import HeuteWidget from "@/pages/widgets/HeuteWidget";
import CookieConsent from "@/components/CookieConsent";
import DisclaimerBanner from "@/components/DisclaimerBanner";

import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Protected from "@/components/Protected";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/heute", label: "Heute", icon: Newspaper, testid: "nav-heute" },
  { to: "/heute?focus=vision", label: "Über Bodenblick", icon: Heart, testid: "nav-about", exact: true },
  { to: "/entdecken", label: "Entdecken", icon: Compass, testid: "nav-entdecken" },
  { to: "/impact", label: "Impact", icon: Sparkle, testid: "nav-impact" },
  { to: "/heritage", label: "Opas Wetter", icon: Clock, testid: "nav-heritage" },
  { to: "/leaderboard", label: "Nachbarn-Liga", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/bees", label: "Bienen-Kalender", icon: Flower, testid: "nav-bees" },
  { to: "/waldbrand", label: "Waldbrand", icon: Fire, testid: "nav-waldbrand" },
  { to: "/myfields", label: "Mein Hof", icon: Barn, testid: "nav-myfields", adminOnly: true },
  { to: "/schulgarten", label: "Unser Schulgarten", icon: Flower, testid: "nav-schulgarten", schoolOnly: true },
  { to: "/marktplatz", label: "Erntepartner", icon: Handshake, testid: "nav-marktplatz" },
  { to: "/markt-akademie", label: "Markt-Akademie", icon: GraduationCap, testid: "nav-markt-akademie" },
  { to: "/lernquiz", label: "Lern-Quiz", icon: Exam, testid: "nav-lernquiz" },
  { to: "/map", label: "Karte", icon: MapTrifold, testid: "nav-map" },
  { to: "/quick", label: "Tages-Ampel", icon: TrafficCone, testid: "nav-quick" },
  { to: "/grassland", label: "Weide & Futter", icon: Cow, testid: "nav-grassland" },
  { to: "/planner", label: "Ernteplaner", icon: CalendarBlank, testid: "nav-planner" },
  { to: "/diseases", label: "Krankheitswarner", icon: Virus, testid: "nav-diseases" },
  { to: "/forecast", label: "Wetter-Ausblick", icon: Snowflake, testid: "nav-forecast" },
  { to: "/satellite", label: "Satellite", icon: GlobeHemisphereEast, testid: "nav-satellite" },
  { to: "/weather", label: "Wetter & Boden", icon: CloudSun, testid: "nav-weather" },
  { to: "/correlation", label: "Correlation", icon: ChartLineUp, testid: "nav-correlation" },
  { to: "/commodities", label: "Rohstoffe", icon: Coin, testid: "nav-commodities" },
  { to: "/compare", label: "Vergleich", icon: GitDiff, testid: "nav-compare" },
  { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts", adminOnly: true },
  { to: "/insights", label: "Weekly Insights", icon: Newspaper, testid: "nav-insights" },
  { to: "/lexikon", label: "Agrar-Lexikon", icon: BookOpen, testid: "nav-lexikon" },
  { to: "/maschinen", label: "Landmaschinen", icon: Wrench, testid: "nav-maschinen" },
  { to: "/podcast", label: "Podcast", icon: Microphone, testid: "nav-podcast" },
  { to: "/academy", label: "Bauern-Academy", icon: BookBookmark, testid: "nav-academy" },
  { to: "/daily-studio", label: "Heute · Studio", icon: Sparkle, testid: "nav-daily-studio", adminOnly: true },
  { to: "/studio", label: "Content Studio", icon: Export, testid: "nav-studio", adminOnly: true },
  { to: "/pipeline", label: "Pipeline", icon: Terminal, testid: "nav-pipeline", adminOnly: true },
  { to: "/settings", label: "Einstellungen", icon: Gear, testid: "nav-settings", adminOnly: true },
];

function Sidebar() {
  const { user, logout } = useAuth();
  return (
    <aside
      className="hidden md:flex md:flex-col w-60 border-r border-white/10 bg-[#0A0A0A] relative z-10"
      data-testid="app-sidebar"
    >
      <div className="px-5 py-5 border-b border-white/10">
        <Link
          to="/"
          data-testid="sidebar-home-link"
          className="block group"
          title="Zurück zur Startseite"
        >
          <div className="eid-label flex items-center gap-1.5 group-hover:text-[#00FA9A] transition-colors">
            <Leaf size={11} weight="fill" className="text-[#00FA9A]" />
            Bodenblick
          </div>
          <div className="mt-1 text-lg font-medium tracking-tight group-hover:text-[#00FA9A] transition-colors">
            Cockpit
          </div>
        </Link>
        <div className="eid-label mt-2 flex items-center justify-between">
          <span>Info-Plattform · v0.5</span>
          <Link
            to="/"
            data-testid="sidebar-home-back"
            className="text-[#00FA9A] hover:underline normal-case tracking-normal text-[10px] inline-flex items-center gap-0.5"
            title="Zurück zur Startseite"
          >
            ← Start
          </Link>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.filter(({ adminOnly, schoolOnly }) => {
          if (adminOnly && !user?.is_admin) return false;
          if (schoolOnly && !user?.is_school) return false;
          return true;
        }).map(({ to, label, icon: Icon, testid, adminOnly, schoolOnly }) => (
          <NavLink
            key={to} to={to} data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                isActive ? "bg-[#00FA9A] text-black" : "text-white hover:bg-[#161616]"
              }`}>
            <Icon size={18} />
            <span className="flex-1">{label}</span>
            {adminOnly && (
              <span className="text-[8px] font-mono uppercase tracking-wider opacity-70">Admin</span>
            )}
            {schoolOnly && (
              <span className="text-[8px] font-mono uppercase tracking-wider opacity-70">Schule</span>
            )}
          </NavLink>
        ))}
        {user?.is_admin && (
          <NavLink
            to="/admin/school"
            data-testid="nav-admin-school"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors mt-2 border-t border-white/10 pt-3 ${
                isActive ? "bg-[#00FA9A] text-black" : "text-[#00FA9A] hover:bg-[#161616]"
              }`}>
            <GraduationCap size={18} weight="fill" />
            <span>Schul-Codes <span className="text-[10px] opacity-70">(Admin)</span></span>
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-2">
        {user && (
          <>
            <div className="flex items-center gap-2 px-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-sm" />
              ) : (
                <div className="w-7 h-7 rounded-sm bg-white/10" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate flex items-center gap-1">
                  {user.name}
                  {user.is_admin && (
                    <span
                      className="inline-flex items-center gap-0.5 bg-[#00FA9A] text-black text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      data-testid="sidebar-admin-badge"
                      title="Du bist Admin"
                    >
                      Admin
                    </span>
                  )}
                  {user.is_demo && (
                    <span
                      className="inline-flex items-center gap-0.5 bg-[#D98324] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      data-testid="sidebar-demo-badge"
                      title="Demo-Zugang"
                    >
                      Demo
                    </span>
                  )}
                  {user.is_school && (
                    <span
                      className="inline-flex items-center gap-0.5 bg-[#FFC24B] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      data-testid="sidebar-school-badge"
                      title={user.school_label || "Schulmodus"}
                    >
                      Schule
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">{user.email}</div>
              </div>
            </div>
            <button onClick={logout} data-testid="sidebar-logout"
              className="w-full flex items-center justify-center gap-1 border border-white/10 text-zinc-400 hover:bg-white/5 rounded-sm px-3 py-2 text-xs">
              <SignOut size={12} /> Abmelden
            </button>
          </>
        )}
        {!user && (
          <div className="text-[10px] text-zinc-500 text-center px-2 leading-relaxed">
            Info-Modus · keine Anmeldung nötig.<br />
            <Link to="/admin-login" className="text-zinc-400 hover:text-white underline" data-testid="sidebar-admin-link">
              Admin-Login
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}

function TopBar() {
  return (
    <div className="md:hidden border-b border-white/10 bg-[#121212] px-4 py-3 flex items-center justify-between" data-testid="app-topbar">
      <Link
        to="/"
        className="flex items-center gap-2 group"
        data-testid="topbar-home-link"
        title="Zurück zur Startseite"
      >
        <span className="p-1.5 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A] group-active:scale-95 transition-transform">
          <Leaf size={14} weight="fill" />
        </span>
        <span className="font-medium group-hover:text-[#00FA9A] transition-colors">Bodenblick</span>
      </Link>
      <Link to="/quick" className="text-xs text-[#00FA9A]">Tages-Ampel</Link>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div className="App flex min-h-screen">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 relative z-10">
        <TopBar />
        <div className="p-4 md:p-6 pb-16 flex-1">{children}</div>
      </main>
      <DisclaimerBanner />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public landing */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Navigate to="/admin-login" replace />} />
      <Route path="/admin-login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/widget/fructan/:regionId" element={<FructanWidget />} />
      <Route path="/widget/pulse" element={<PulseWidget />} />
      <Route path="/widget/wildfires" element={<WildfireWidget />} />
      <Route path="/widget/heute" element={<HeuteWidget />} />
      <Route path="/embed" element={<EmbedHub />} />
      <Route path="/wrapped" element={<Wrapped />} />
      <Route path="/wrapped/:year" element={<Wrapped />} />
      <Route path="/schulgaerten" element={<SchoolGardenMap />} />
      <Route path="/feldfruechte" element={<Feldfruechte />} />
      <Route path="/glossar" element={<Glossary />} />

      {/* Magazine-style public reads */}
      <Route path="/insights" element={<PublicInsights />} />
      <Route path="/insights/:slug" element={<InsightPost />} />
      <Route path="/heute" element={<Layout><DailyToday /></Layout>} />
      <Route path="/heute/:date" element={<Layout><DailyToday /></Layout>} />
      <Route path="/daily-studio" element={<Protected requireAdmin><Layout><DailyStudio /></Layout></Protected>} />
      <Route path="/quick" element={<QuickStatus />} />

      {/* Info-Modus: alle Inhalte ohne Anmeldung sichtbar.
          Schreib-Aktionen (Polygon zeichnen, Inserate, Memos) sind in den
          Page-Komponenten je nach `user.is_admin` ausgeblendet. */}
      <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
      <Route path="/weather" element={<Layout><Weather /></Layout>} />
      <Route path="/correlation" element={<Layout><Correlation /></Layout>} />
      <Route path="/satellite" element={<Layout><Satellite /></Layout>} />
      <Route path="/studio" element={<Protected requireAdmin><Layout><ContentStudio /></Layout></Protected>} />
      <Route path="/pipeline" element={<Protected requireAdmin><Layout><Pipeline /></Layout></Protected>} />
      <Route path="/lexikon" element={<Layout><Lexikon /></Layout>} />
      <Route path="/map" element={<Layout><RegionMap /></Layout>} />
      <Route path="/settings" element={<Protected requireAdmin><Layout><Settings /></Layout></Protected>} />
      <Route path="/forecast" element={<Layout><Forecast /></Layout>} />
      <Route path="/compare" element={<Layout><Compare /></Layout>} />
      <Route path="/alerts" element={<Protected requireAdmin><Layout><Alerts /></Layout></Protected>} />
      <Route path="/myfields" element={<Protected requireAdmin><Layout><MyFields /></Layout></Protected>} />
      <Route path="/schulgarten" element={<Protected requireSchool><Layout><SchoolGarden /></Layout></Protected>} />
      <Route path="/entdecken" element={<Layout><Entdecken /></Layout>} />
      <Route path="/impact" element={<Layout><Impact /></Layout>} />
      <Route path="/heritage" element={<Layout><Heritage /></Layout>} />
      <Route path="/leaderboard" element={<Layout><Leaderboard /></Layout>} />
      <Route path="/bees" element={<Layout><Bees /></Layout>} />
      <Route path="/waldbrand" element={<Layout><Waldbrand /></Layout>} />
      <Route path="/field-report/:fieldId" element={<FieldReport />} />
      <Route path="/school" element={<SchoolEnter />} />
      <Route path="/admin/school" element={<Protected requireAdmin><Layout><SchoolAdmin /></Layout></Protected>} />
      <Route path="/marktplatz" element={<Layout><Marketplace /></Layout>} />
      <Route path="/journal" element={<Protected requireAdmin><Layout><Journal /></Layout></Protected>} />
      <Route path="/commodities" element={<Layout><Commodities /></Layout>} />
      <Route path="/markt-akademie" element={<Layout><MarketAcademy /></Layout>} />
      <Route path="/lernquiz" element={<Layout><LernQuiz /></Layout>} />
      <Route path="/maschinen" element={<Layout><Maschinen /></Layout>} />
      <Route path="/grassland" element={<Layout><Grassland /></Layout>} />
      <Route path="/academy" element={<Layout><Academy /></Layout>} />
      <Route path="/podcast" element={<Layout><Podcast /></Layout>} />
      <Route path="/planner" element={<Layout><Planner /></Layout>} />
      <Route path="/diseases" element={<Layout><DiseaseWatch /></Layout>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}
